import React, { ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import GraphiteHeader from './GraphiteHeader';
import GraphiteAside from './GraphiteAside';
import './graphite-chrome.scss';

interface GraphiteChromeProps {
  children: ReactNode;
}

/**
 * GraphiteChrome — the layout shell that replaces Metronic's MasterLayout
 * internals when the Graphite theme is active (mirrors TerminalChrome). Owns
 * its own translucent header + sidebar; `{children}` is the route outlet,
 * identical to the default layout, so routes don't know which chrome wraps them.
 *
 * Only state held here is the mobile-aside toggle.
 */
const GraphiteChrome: React.FC<GraphiteChromeProps> = ({ children }) => {
  const [mobileAsideOpen, setMobileAsideOpen] = useState(false);
  const location = useLocation();

  // Dismiss the mobile aside whenever the route changes.
  useEffect(() => { setMobileAsideOpen(false); }, [location.pathname]);

  // Graphite is a single warm palette — pin Bootstrap's mode to `light` while
  // it's active so the many legacy `[data-bs-theme="dark"]`-gated hard-coded
  // dark surfaces across pages (Contacts inputs, task modals, etc.) don't paint
  // dark patches under the warm theme. Graphite's CSS-var overrides then sit on
  // top of the light defaults. Restored on unmount (switch to another theme).
  useEffect(() => {
    const root = document.documentElement;
    const prev = root.getAttribute('data-bs-theme');
    root.setAttribute('data-bs-theme', 'light');
    return () => { if (prev) root.setAttribute('data-bs-theme', prev); };
  }, []);

  return (
    <div className="g-chrome">
      <GraphiteHeader
        onMenuToggle={() => setMobileAsideOpen((o) => !o)}
        mobileMenuOpen={mobileAsideOpen}
      />
      <div className="g-chrome__body">
        <GraphiteAside mobileOpen={mobileAsideOpen} onClose={() => setMobileAsideOpen(false)} />
        <main className="g-chrome__content">{children}</main>
      </div>
    </div>
  );
};

export default GraphiteChrome;
